// src/services/CacheManager.js
class CacheManager {
  constructor() {
    // Memory cache - fastest access
    this.memoryCache = new Map();
    
    // Default TTL: 5 minutes
    this.defaultTTL = 5 * 60 * 1000;
  }

  /**
   * Generate cache key with subscriber isolation
   */
  _getCacheKey(key, subscriberId) {
    return `${subscriberId}_${key}`;
  }

  /**
   * Check if cached data is stale
   */
  _isStale(timestamp, ttl = this.defaultTTL) {
    return Date.now() - timestamp > ttl;
  }

  /**
   * Get data from cache (memory first, then localStorage)
   */
  get(key, subscriberId, ttl) {
    const cacheKey = this._getCacheKey(key, subscriberId);
    
    // Try memory cache first (fastest)
    if (this.memoryCache.has(cacheKey)) {
      const cached = this.memoryCache.get(cacheKey);
      if (!this._isStale(cached.timestamp, ttl)) {
        return {
          data: cached.data,
          fromCache: true,
          source: 'memory'
        };
      } else {
        // Stale memory cache, remove it
        this.memoryCache.delete(cacheKey);
      }
    }

    // Try localStorage (persistent)
    try {
      const stored = localStorage.getItem(cacheKey);
      if (stored) {
        const cached = JSON.parse(stored);
        if (!this._isStale(cached.timestamp, ttl)) {
          // Store in memory for faster next access
          this.memoryCache.set(cacheKey, cached);
          return {
            data: cached.data,
            fromCache: true,
            source: 'localStorage'
          };
        } else {
          // Stale localStorage cache, remove it
          localStorage.removeItem(cacheKey);
        }
      }
    } catch (error) {
      console.warn('Error reading from localStorage:', error);
    }

    return {
      data: null,
      fromCache: false,
      source: null
    };
  }

  /**
   * Store data in both memory and localStorage
   */
  set(key, data, subscriberId) {
    const cacheKey = this._getCacheKey(key, subscriberId);
    const cached = {
      data,
      timestamp: Date.now(),
      subscriberId
    };

    // Store in memory cache
    this.memoryCache.set(cacheKey, cached);

    // Store in localStorage (with error handling)
    try {
      localStorage.setItem(cacheKey, JSON.stringify(cached));
    } catch (error) {
      console.warn('Error writing to localStorage:', error);
      // Continue anyway, we still have memory cache
    }
  }

  /**
   * Invalidate specific cache entry
   */
  invalidate(key, subscriberId) {
    const cacheKey = this._getCacheKey(key, subscriberId);
    
    // Remove from memory
    this.memoryCache.delete(cacheKey);
    
    // Remove from localStorage
    try {
      localStorage.removeItem(cacheKey);
    } catch (error) {
      console.warn('Error removing from localStorage:', error);
    }
  }

  /**
   * Clear all cache for a subscriber (when switching accounts)
   */
  clearSubscriber(subscriberId) {
    // Clear memory cache
    for (const [key] of this.memoryCache) {
      if (key.startsWith(`${subscriberId}_`)) {
        this.memoryCache.delete(key);
      }
    }

    // Clear localStorage
    try {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(`${subscriberId}_`)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch (error) {
      console.warn('Error clearing localStorage:', error);
    }
  }

  /**
   * Clear all caches (useful for logout)
   */
  clearAll() {
    // Clear memory
    this.memoryCache.clear();
    
    // Clear localStorage (only our cache entries)
    try {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.includes('_operators') || key.includes('_teams') || 
            key.includes('_templates') || key.includes('_customers'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch (error) {
      console.warn('Error clearing all localStorage:', error);
    }
  }

  /**
   * Get cache statistics (useful for debugging)
   */
  getStats() {
    const memorySize = this.memoryCache.size;
    let localStorageSize = 0;
    
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.includes('_operators') || key.includes('_teams') || 
                   key.includes('_templates') || key.includes('_customers'))) {
          localStorageSize++;
        }
      }
    } catch (error) {
      console.warn('Error getting localStorage stats:', error);
    }

    return {
      memoryEntries: memorySize,
      localStorageEntries: localStorageSize,
      defaultTTL: this.defaultTTL
    };
  }
}

// Create singleton instance
const cacheManager = new CacheManager();
export default cacheManager;